// Physicians tab — the directory list, and the record panel that opens from
// a row: who they see, what has been faxed to their office, and the actions
// that otherwise take three pages (edit, verify, merge a duplicate).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../DataTable';
import {
  listPhysicians, listFacilities, markVerified, mergePhysicians, getPhysicianClients, getPhysicianFaxes,
  searchPhysicians, fmtPhone, CARE_ROLE_LABEL,
} from '../../lib/directory';
import { STATUS_LABEL as FAX_STATUS } from '../../lib/faxes';
import { RefCode, Pill, VerifiedPill, Phone, Chip, Modal, Empty, label } from './ui';
import PhysicianForm from './PhysicianForm';
import PhysicianPicker from './PhysicianPicker';

const ROLE_TONE = { pcp: 'info', ordering: 'copper', specialist: 'neutral', referring: 'ok', other: 'neutral' };

export default function PhysiciansTab({ canEdit, openId }) {
  const { profile } = useAuth();
  const actor = profile?.full_name || 'staff';
  const [rows, setRows] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [panel, setPanel] = useState(null);       // physician row
  const [editing, setEditing] = useState(null);   // { physician } | { new: true, query }

  const load = async () => {
    const [{ data: p }, { data: f }] = await Promise.all([listPhysicians(), listFacilities()]);
    setRows(p || []); setFacilities(f || []); setLoading(false);
    return p || [];
  };
  useEffect(() => { load().then(p => { if (openId) { const hit = p.find(x => x.id === openId); if (hit) setPanel(hit); } }); }, [openId]);

  const visible = useMemo(() => searchPhysicians(rows, q).filter(p =>
    (showInactive || p.status === 'active') &&
    (filter === 'all' || (filter === 'missing_npi' && !p.npi) || (filter === 'needs_check' && !p.verified_at) || (filter === 'no_fax' && !p.fax))
  ), [rows, q, filter, showInactive]);

  const columns = useMemo(() => [
    { accessorKey: 'ref_code', header: 'Ref', size: 70, cell: ({ getValue }) => <RefCode code={getValue()} />, meta: { mobile: 'primary', mono: true } },
    { accessorKey: 'display_name', header: 'Physician', size: 210, meta: { mobile: 'primary' },
      cell: ({ row }) => <div><div style={{ fontWeight: 500 }}>{row.original.display_name}{row.original.credentials ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> {row.original.credentials}</span> : null}</div></div> },
    { accessorKey: 'specialty', header: 'Specialty', size: 170, meta: { truncate: true } },
    { accessorKey: 'npi', header: 'NPI', size: 110, cell: ({ getValue }) => getValue() || <Pill tone="bad">missing</Pill>, meta: { mono: true } },
    { accessorKey: 'facility_name', header: 'Facility', size: 240, meta: { truncate: true },
      cell: ({ row }) => row.original.facility_name ? <span><RefCode code={row.original.facility_ref} /> {row.original.facility_name}</span> : <span style={{ color: 'var(--text3)' }}>—</span> },
    { accessorKey: 'phone', header: 'Phone', size: 130, cell: ({ getValue }) => <Phone value={getValue()} /> },
    { accessorKey: 'fax', header: 'Fax', size: 130, cell: ({ getValue }) => <Phone value={getValue()} /> },
    { accessorKey: 'email', header: 'Email', size: 180, meta: { truncate: true }, cell: ({ getValue }) => getValue() || <span style={{ color: 'var(--text3)' }}>—</span> },
    { accessorKey: 'client_count', header: 'Clients', size: 80, meta: { align: 'right' } },
    { accessorKey: 'verified_at', header: 'Verified', size: 120, cell: ({ getValue }) => <VerifiedPill verified_at={getValue()} /> },
  ], []);

  const counts = useMemo(() => ({
    missing_npi: rows.filter(p => p.status === 'active' && !p.npi).length,
    needs_check: rows.filter(p => p.status === 'active' && !p.verified_at).length,
    no_fax: rows.filter(p => p.status === 'active' && !p.fax).length,
  }), [rows]);

  const afterSave = async (saved) => {
    setEditing(null);
    const fresh = await load();
    if (panel) setPanel(fresh.find(p => p.id === (saved?.id || panel.id)) || null);
  };
  const verify = async (p) => { await markVerified('physicians', p.id, actor); setEditing(null); const fresh = await load(); setPanel(fresh.find(x => x.id === p.id) || null); };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="form-input" style={{ flex: 1, minWidth: 220 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, NPI, practice, specialty, phone or fax…" />
        <Chip on={filter === 'all'} onClick={() => setFilter('all')}>All</Chip>
        <Chip on={filter === 'missing_npi'} onClick={() => setFilter('missing_npi')}>Missing NPI <b>{counts.missing_npi}</b></Chip>
        <Chip on={filter === 'no_fax'} onClick={() => setFilter('no_fax')}>No fax <b>{counts.no_fax}</b></Chip>
        <Chip on={filter === 'needs_check'} onClick={() => setFilter('needs_check')}>Needs check <b>{counts.needs_check}</b></Chip>
        <label style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> inactive</label>
        {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setEditing({ new: true, query: q })}>+ Add Physician</button>}
      </div>
      <DataTable tableId="directory-physicians" columns={columns} data={visible} getRowId={r => r.id} loading={loading}
        onRowClick={p => setPanel(p)} maxHeight="calc(100vh - 300px)"
        rowStyle={p => p.status === 'inactive' ? { opacity: 0.55 } : undefined}
        emptyMessage={rows.length ? 'No physicians match.' : 'No physicians yet.'} />

      {panel && !editing && (
        <PhysicianPanel physician={panel} canEdit={canEdit} actor={actor} physicians={rows}
          onClose={() => setPanel(null)} onEdit={() => setEditing({ physician: panel })} onVerify={() => verify(panel)}
          onMerged={async () => { setPanel(null); await load(); }} />
      )}
      {editing?.new && <PhysicianForm initialQuery={editing.query} facilities={facilities} onSaved={afterSave} onClose={() => setEditing(null)} />}
      {editing?.physician && <PhysicianForm physician={editing.physician} facilities={facilities} onSaved={afterSave} onClose={() => setEditing(null)} onVerify={canEdit ? verify : undefined} />}
    </div>
  );
}

// ------------------------------------------------------------ record panel
export function PhysicianPanel({ physician: p, canEdit, actor, physicians, onClose, onEdit, onVerify, onMerged }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [faxes, setFaxes] = useState(null);
  const [merge, setMerge] = useState(null);   // target physician
  const [mergeErr, setMergeErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setClients(null); setFaxes(null);
    getPhysicianClients(p.id).then(({ data }) => setClients(data || []));
    getPhysicianFaxes(p.fax).then(({ data }) => setFaxes(data || []));
  }, [p.id, p.fax]);

  const doMerge = async () => {
    if (!merge) return;
    setBusy(true); setMergeErr('');
    const { error } = await mergePhysicians(p.id, merge.id, actor);
    setBusy(false);
    if (error) { setMergeErr(error.message); return; }
    onMerged();
  };

  const kv = (k, v) => (
    <div style={{ display: 'contents' }}><div style={{ fontSize: 12, color: 'var(--text3)' }}>{k}</div><div style={{ fontSize: 13, minWidth: 0 }}>{v}</div></div>
  );

  return (
    <Modal onClose={onClose} width={640}
      title={<><RefCode code={p.ref_code} /><span>{p.display_name}{p.credentials ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> {p.credentials}</span> : null}</span>{p.status === 'inactive' && <Pill tone="bad">inactive</Pill>}</>}
      footer={<>
        {canEdit && <button className="btn btn-outline" style={{ marginRight: 'auto', color: 'var(--danger)', borderColor: 'var(--danger-border)' }} onClick={() => setMerge(false)}>Merge into another record…</button>}
        {canEdit && !p.verified_at && <button className="btn btn-outline" style={{ color: 'var(--success)', borderColor: 'var(--success-border)' }} onClick={onVerify}>✓ Details confirmed today</button>}
        {canEdit && <button className="btn btn-primary" onClick={onEdit}>✎ Edit</button>}
      </>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <VerifiedPill verified_at={p.verified_at} />
        {p.verified_by && p.verified_at && <span style={{ fontSize: 12, color: 'var(--text3)' }}>by {p.verified_by}</span>}
        {p.npi ? <Pill tone={p.nppes_checked_at ? 'ok' : 'neutral'}>{p.nppes_checked_at ? 'NPPES ✓' : 'NPI on file'}</Pill> : <Pill tone="bad">no NPI</Pill>}
        {p.source?.startsWith('backfill') && <Pill tone="neutral">from clean-up</Pill>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: '6px 12px', marginTop: 14 }}>
        {kv('Specialty', p.specialty || '—')}
        {kv('NPI', p.npi ? <span style={{ fontFamily: 'var(--mono)' }}>{p.npi}</span> : '—')}
        {kv('Facility', p.facility_name ? <span><RefCode code={p.facility_ref} /> {p.facility_name}</span> : '—')}
        {kv('Address', [p.address_line1, p.address_line2, [p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(', ') || '—')}
        {kv('Phone', <Phone value={p.phone} />)}
        {kv('Fax', <Phone value={p.fax} />)}
        {kv('Email', p.email || '—')}
        {kv('Best route', p.preferred_contact ? p.preferred_contact[0].toUpperCase() + p.preferred_contact.slice(1) : '—')}
        {p.notes && kv('Notes', <span style={{ whiteSpace: 'pre-wrap', color: 'var(--text2)' }}>{p.notes}</span>)}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 12 }}>
        <div style={label}>Clients · {clients ? clients.length : '…'}</div>
        {clients === null ? null : clients.length === 0 ? <Empty>No client is linked to this physician.</Empty> : clients.map(l => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px dashed var(--border)', fontSize: 13 }}>
            <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => navigate(`/clients/${l.client.id}`)}>
              {l.client.last_name}, {l.client.first_name} <RefCode code={l.client.client_id} />
            </span>
            <Pill tone={ROLE_TONE[l.role] || 'neutral'}>{CARE_ROLE_LABEL[l.role] || l.role}</Pill>
          </div>
        ))}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 12 }}>
        <div style={label}>Recent faxes to {p.fax ? fmtPhone(p.fax) : 'this office'}</div>
        {!p.fax ? <Empty>No fax number on record.</Empty> : faxes === null ? null : faxes.length === 0 ? <Empty>Nothing has been faxed to this number yet.</Empty> : faxes.map(f => (
          <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px dashed var(--border)', fontSize: 13 }}>
            <span>{f.request_title || f.doc_type || 'Fax'}{f.client ? <span style={{ color: 'var(--text3)' }}> · {f.client.last_name}, {f.client.first_name} ({f.client.client_id})</span> : null}</span>
            <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{new Date(f.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} · {FAX_STATUS[(f.faxage_status || '').toLowerCase()] || f.faxage_status || '—'}</span>
          </div>
        ))}
      </div>

      {merge !== null && (
        <div style={{ marginTop: 16, border: '1px solid var(--danger-border)', borderRadius: 10, padding: 12, background: 'var(--danger-bg)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Merge {p.display_name} into…</div>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 8 }}>Every client, intake, order and queue item pointing at this record moves to the one you pick, and this record is retired. Pick the record with the right NPI.</div>
          <PhysicianPicker physicians={physicians.filter(x => x.status === 'active')} value={merge || null} onChange={setMerge} exclude={[p.id]} autoFocus />
          {mergeErr && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{mergeErr}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setMerge(null)}>Cancel</button>
            <button className="btn btn-sm" style={{ background: 'var(--danger)', color: '#fff' }} disabled={!merge || busy} onClick={doMerge}>{busy ? 'Merging…' : `Merge into ${merge?.display_name || '…'}`}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
